import React, { useState, useEffect, useMemo } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, Clock, RotateCcw } from 'lucide-react';
import api from '../../lib/api';
import { toast } from 'sonner';

const formatCurrency = (amt) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amt || 0);

/**
 * Manual Overtime override modal — replaces the attendance-driven OT
 * with a fixed amount on a single pending/processed payroll row. DELETE
 * reverts to the auto value. Locked on `paid` rows by the backend
 * (returns 403; we hide the menu entry too).
 */
export default function OvertimeOverrideModal({ open, onClose, payroll, onSaved }) {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Pre-fill with the current override (or the auto value as a hint
    // when none is set yet).
    if (payroll?.overtime_pay_override !== null && payroll?.overtime_pay_override !== undefined) {
      setAmount(String(payroll.overtime_pay_override));
    } else {
      setAmount(String(payroll?.overtime_pay_auto ?? payroll?.overtime_pay ?? 0));
    }
    setNote('');
  }, [open, payroll]);

  const autoValue = Number(payroll?.overtime_pay_auto ?? payroll?.overtime_pay ?? 0);
  const hasOverride = payroll?.overtime_pay_override !== null
    && payroll?.overtime_pay_override !== undefined;
  const previewNet = useMemo(() => {
    if (!payroll) return 0;
    const ot = Number(amount) || 0;
    return (Number(payroll.monthly_gross) || 0)
      + ot
      + (Number(payroll.arrears_amount) || 0)
      - (Number(payroll.total_deductions) || 0);
  }, [amount, payroll]);

  const handleSave = async () => {
    const amt = Number(amount);
    if (Number.isNaN(amt) || amt < 0) {
      toast.error('Amount must be a non-negative number');
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(`/payroll/${payroll.id}/overtime-override`, {
        amount: amt, note,
      });
      toast.success(r.data?.message || 'Overtime override saved');
      onSaved?.(r.data?.data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save override');
    } finally {
      setSaving(false);
    }
  };

  const handleRevert = async () => {
    setSaving(true);
    try {
      const r = await api.delete(`/payroll/${payroll.id}/overtime-override`);
      toast.success(r.data?.message || 'Override cleared — reverted to auto value');
      onSaved?.(r.data?.data);
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clear override');
    } finally {
      setSaving(false);
    }
  };

  if (!payroll) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="ot-override-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-500" />
            Overtime Override — {payroll.employee_name}
          </DialogTitle>
          <DialogDescription>
            {payroll.month} {payroll.year} · Manually set the overtime amount paid out for this cycle.
            The attendance-driven auto value is <strong>{formatCurrency(autoValue)}</strong>.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="ot-amount">Overtime amount (₹)</Label>
            <Input
              id="ot-amount" type="number" min={0} step={0.01}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              data-testid="ot-override-amount"
            />
            <p className="text-xs text-muted-foreground">
              Enter <strong>0</strong> to explicitly zero out the OT; clear the override entirely with the button below to revert to the auto value.
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ot-note">Note (optional)</Label>
            <Input
              id="ot-note" placeholder="e.g. Approved by HR — extra weekend coverage"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              data-testid="ot-override-note"
            />
          </div>

          <div className="rounded-lg border bg-muted/30 p-3 space-y-1" data-testid="ot-preview">
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>Auto OT (from attendance)</span>
              <span>{formatCurrency(autoValue)}</span>
            </div>
            <div className="flex justify-between text-sm font-medium">
              <span>New OT value</span>
              <span className="text-amber-600">{formatCurrency(Number(amount) || 0)}</span>
            </div>
            <div className="flex justify-between font-semibold pt-2 border-t mt-2">
              <span>New Net Salary</span>
              <span data-testid="ot-net-preview">{formatCurrency(previewNet)}</span>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          {hasOverride && (
            <Button
              type="button" variant="ghost" onClick={handleRevert} disabled={saving}
              className="text-amber-600"
              data-testid="ot-revert"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              Revert to auto
            </Button>
          )}
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="ot-override-save">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            Save override
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
